import React, { ReactNode } from "react"
import { ApolloClient, InMemoryCache, ApolloLink } from "@apollo/client"
import { ApolloProvider } from "@apollo/client/react"
import { ErrorLink } from "@apollo/client/link/error"
import { CombinedGraphQLErrors } from "@apollo/client/errors"
import { createAuthLink } from "aws-appsync-auth-link"
import { createSubscriptionHandshakeLink } from "aws-appsync-subscription-link"
import merge from "lodash/merge"
import type { Common } from "lib/types"
import { useConfig } from "../../configuration/useConfig"
import { fetchAuthSession } from "aws-amplify/auth"
import { RemoveTypenameFromVariablesLink } from "@apollo/client/link/remove-typename"
import { AwsRumClient } from "helpers/awsRumClient"

type Props = {
  children: ReactNode
}

const ApolloClientProvider = ({ children }: Props) => {
  const { awsRegion, graphqlApiUrl, rumAppMonitorId, rumIdentityPool } = useConfig().config
  const url: string = graphqlApiUrl
  const region: string = awsRegion
  const auth: Common.AuthOptions = {
    type: "OPENID_CONNECT",
    jwtToken: async () =>
      (await fetchAuthSession().then((res) =>
        res.tokens?.accessToken.toString()
      )) ?? ""
  }

  const parseCustomerAttribute = (value: string | object): object => {
    switch (typeof value) {
      case "string":
        return parseCustomerAttribute(JSON.parse(value))
      case "object":
        return value
      default:
        return {}
    }
  }

  const removeTypenameLink = new RemoveTypenameFromVariablesLink()

  const client = new ApolloClient({
    link: ApolloLink.from([
      removeTypenameLink,
      new ErrorLink(({ error, operation }) => {
        const { skipRumLogging } = operation.getContext() || {};
        const rumClient = AwsRumClient(rumAppMonitorId, rumIdentityPool, awsRegion);

        if (CombinedGraphQLErrors.is(error)) {
          // Handle GraphQL errors
          if (!skipRumLogging) {
            error.errors.forEach((err) => {
              const operationName = operation?.operationName ?? "unknown"
              const route = window.location.pathname

              const enrichedMessage = [
                err.message,
                `Operation: ${operationName}`
              ].join(" | ")

              const errorObj = new Error(enrichedMessage)
              errorObj.name = "GraphQLError"
              errorObj.stack = `Route: ${route} | Variables: ${JSON.stringify(operation.variables)}`
              rumClient.recordError(errorObj)

              console.error(
                `[GraphQL error]: Message: ${err.message}, Location: ${JSON.stringify(
                  err.locations
                )}, Path: ${err.path}`
              )
            })
          }
        } else {
          // Handle network errors
          // Ignore AbortError - these are expected when queries are cancelled
          if (error.name === "AbortError") {
            return
          }

          // Ignore UnconventionalError - these are expected from AppSync subscriptions
          if (error.name === "UnconventionalError" || error.message?.includes("UnconventionalError")) {
            return
          }

          rumClient.recordError(error)
          console.log(`[Network error]: ${JSON.stringify(error)}`) // eslint-disable-line
        }
      }),
      createAuthLink({ url, region, auth }),
      createSubscriptionHandshakeLink({ url, region, auth })
    ]),
    cache: new InMemoryCache({
      typePolicies: {
        ShippingFeeModificationUpdateData: {
          keyFields: false,
        },
        PaymentProvider: {
          keyFields: false,
        },
        Provider: {
          keyFields: false,
        },
        QueryProductManagement: {
          merge: true
        },
        QueryOrderManagement: {
          merge: true
        },
        OrderLineModificationUpdateData: {
          keyFields: false
        },
        OrderLineModificationUpdateDataPrice: {
          keyFields: false
        },
        OrderLineModificationCreateData: {
          keyFields: false
        },
        OrderLineModificationDeleteData: {
          keyFields: false
        },
        OrderLineModification: {
          keyFields: false
        },
        OrderShippingAvardaShippingBroker: {
          merge: true
        },
        ShopperInsightsSearchHitCheckoutItem: {
          keyFields: false,
        },
        CompensationPayment: {
          keyFields: false,
        },
        Order: {
          fields: {
            shippingAddress: {
              merge: true
            },
            customerAttribute: {
              merge(existing, incoming) {
                return merge({}, parseCustomerAttribute(existing), parseCustomerAttribute(incoming))
              }
            },
          }
        }
      }
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: false
      }
    }
  })

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}

export { ApolloClientProvider }
